import { z } from 'zod';
import {
  bayCode,
  batchResultKind,
  devEui,
  isoDateTime,
  paginated,
  problemBody,
  uuid,
  zoneCode,
} from '@/api/schemas/common';

/** Devices, mapping and commissioning — shared-contract.md §4. */

export const deviceSchema = z.object({
  devEui,
  /** The serial printed on the sensor's label (backend feature 016, 7 October 2026). Null until
   *  the sensor has sent its device-info frame — never guessed from the DevEUI. */
  serialNumber: z.string().nullable(),
  deviceType: z.string(),
  /** Null for a spare that has never joined — a row without a first contact. */
  firstSeenAt: isoDateTime.nullable(),
  lastSeenAt: isoDateTime.nullable(),
  batteryPct: z.number().int().min(0).max(100).nullable(),
  bestRssi: z.number().nullable(),
  bestSnr: z.number().nullable(),
  gatewayCount: z.number().int().nonnegative(),
  mappedToBayCode: bayCode.nullable(),
  mappedToZoneCode: zoneCode.nullable(),
  health: z.enum(['ok', 'battery', 'weak', 'silent', 'never_heard', 'spare']),
});

export const deviceListSchema = paginated(deviceSchema);

export const devicePairingSchema = z.object({
  id: uuid,
  devEui,
  bayCode,
  boundAt: isoDateTime,
  boundBy: z.string(),
  /** Nothing is deleted: a release closes the pairing. This history is the
   *  as-built register. */
  releasedAt: isoDateTime.nullable(),
  releasedBy: z.string().nullable(),
  releaseReason: z.string().nullable(),
});

export const deviceDetailSchema = deviceSchema.extend({
  uplinks24h: z.number().int().nonnegative(),
  rejectionPct: z.number(),
  frameCounter: z.number().int().nonnegative(),
  gatewaysHeardBy: z.array(z.string()),
  mappingHistory: z.array(devicePairingSchema),
});

/** `POST /devices/{devEui}/map` — one call carrying both ids. */
export const deviceMapRequestSchema = z.object({
  clientRequestId: z.string().min(1),
  bayCode,
  calibrateNow: z.boolean().default(false),
});

export const deviceMapResponseSchema = z.object({
  devEui,
  bayCode,
  boundAt: isoDateTime,
  boundBy: z.string(),
  /** A bay whose device changes has its calibration invalidated. */
  needsCalibration: z.boolean(),
  decommissioned: z.object({ devEui, releasedAt: isoDateTime, reason: z.string() }).nullable(),
});

export const deviceUnmapRequestSchema = z.object({
  clientRequestId: z.string().min(1),
  reason: z.string().min(1),
});

/** `POST /commissioning/batch` — up to 50 items, in submitted order. */
export const commissioningBatchRequestSchema = z.object({
  items: z
    .array(
      z.object({
        clientRequestId: z.string().min(1),
        devEui,
        bayCode,
        calibrateNow: z.boolean().optional(),
      }),
    )
    .max(50),
});

export const commissioningBatchResponseSchema = z.object({
  results: z.array(
    z.object({
      clientRequestId: z.string(),
      /** `duplicate` is a success, not an error. */
      result: batchResultKind,
      pairing: deviceMapResponseSchema.optional(),
      problem: problemBody.optional(),
    }),
  ),
});

/** `GET /commissioning/progress` */
export const commissioningProgressSchema = z.object({
  observedAt: isoDateTime,
  mapped: z.number().int().nonnegative(),
  total: z.number().int().nonnegative(),
  mappedPct: z.number(),
  /**
   * Mapped AND has ever produced a plausible reading — not merely "has ever produced telemetry".
   * A device stuck on the tilt sentinel (65533/65535) reports but never plausibly; see
   * `implausibleDevices` for exactly those. Found live, 30 September 2026, real Promenade 04/05
   * sensors.
   */
  firstContactConfirmed: z.number().int().nonnegative(),
  /** Mapped but never heard at all — "check the install". */
  silent: z.number().int().nonnegative(),
  calibrated: z.number().int().nonnegative(),
  awaitingEmptyBay: z.number().int().nonnegative(),
  areasComplete: z.number().int().nonnegative(),
  areasTotal: z.number().int().nonnegative(),
  completeAreaCodes: z.array(zoneCode),
  /**
   * When the last bay was mapped, confirmed and calibrated — `null` while the
   * job is still running. The dashboard tile lives while commissioning is
   * incomplete and for thirty days after this instant (W-2.6).
   */
  completedAt: isoDateTime.nullable(),
  silentDevices: z.array(z.object({ bayCode, devEui, mappedAt: isoDateTime })),
  /** Reported, but never plausibly — distinct from `silentDevices` (never reported at all).
   * "Never heard" and "heard only garbage" are different problems with different fixes on site. */
  implausibleDevices: z.array(
    z.object({
      bayCode, devEui, mappedAt: isoDateTime,
      lastDistanceMm: z.number().int().nullable(),
      lastPosition: z.string().nullable(),
    }),
  ),
  byArea: z.array(
    z.object({
      zoneCode,
      zoneName: z.string(),
      mapped: z.number().int().nonnegative(),
      bays: z.number().int().nonnegative(),
    }),
  ),
});

export type Device = z.infer<typeof deviceSchema>;
export type DeviceDetail = z.infer<typeof deviceDetailSchema>;
export type DevicePairing = z.infer<typeof devicePairingSchema>;
export type CommissioningProgress = z.infer<typeof commissioningProgressSchema>;
